// Overlay construction and merging. The overlay is the evaluator's derived
// output; mergeEffective() combines it with canonical state into the
// *effective* configuration — forced picks over user picks, forced variants,
// and auto-attached slot contents materialized as synthetic instances. The
// evaluator itself re-merges per fixed-point pass so chained rules see the
// effective state; UI consumers merge once per evaluation to render.
//
// Auto-attached products are overlay, but user edits on them are canonical,
// keyed by the stable pair (hookKey, ruleId) — see stableAutoAttachKey().

import type {
  ConfigInstance,
  ConfigurationState,
  InstanceOverlay,
  Overlay,
} from "./types";

export function emptyOverlay(): Overlay {
  return { instances: {}, sceneFlags: [] };
}

export function emptyInstanceOverlay(): InstanceOverlay {
  return {
    forcedPicks: {},
    lockedSurfaces: {},
    restrictedMaterials: {},
    forcedVariant: null,
    forcedSlotContents: {},
    restrictedOptions: {},
    blockedAttachments: [],
    flags: [],
  };
}

/** Get (creating lazily) the overlay bucket for an instance. Buckets only
 *  exist where a rule wrote something, so "no rules fired" reads as an
 *  overlay with zero instance entries. */
export function getInstanceOverlay(overlay: Overlay, instanceId: string): InstanceOverlay {
  let io = overlay.instances[instanceId];
  if (!io) {
    io = emptyInstanceOverlay();
    overlay.instances[instanceId] = io;
  }
  return io;
}

/** Stable identity for an auto-attached (overlay-spawned) instance. Keyed by
 *  (hookKey, ruleId) — not a transient instance id — so if the trigger flips
 *  away and back, the auto-attached product returns with its canonical
 *  customization intact. */
export function stableAutoAttachKey(hookKey: string, ruleId: string): string {
  return `auto:${hookKey}:${ruleId}`;
}

/** Apply an instance's overlay bucket to produce its effective shape:
 *  forced picks win over canonical picks, forced variant over chosen variant,
 *  auto-attached contents over (or into) canonical slot contents. */
function applyInstanceOverlay(
  inst: ConfigInstance,
  io: InstanceOverlay | undefined,
): ConfigInstance {
  const out: ConfigInstance = {
    ...inst,
    materialPicks: { ...inst.materialPicks },
    slotContents: { ...inst.slotContents },
  };
  if (!io) return out;
  for (const [surfaceKey, entry] of Object.entries(io.forcedPicks)) {
    out.materialPicks[surfaceKey] = entry.materialId;
  }
  if (io.forcedVariant) out.variantKey = io.forcedVariant.variantKey;
  for (const [hookKey, entry] of Object.entries(io.forcedSlotContents)) {
    out.slotContents[hookKey] = { productId: entry.productId, instanceId: entry.attachKey };
  }
  return out;
}

/** Merge canonical state with an overlay into the effective configuration.
 *  Auto-attached products become synthetic instances (instanceId = their
 *  stable attach key) appended after the canonical instances, so relations
 *  (slots/assembly/scene) and per-instance rules see them like any other
 *  instance. Nested auto-attach (a synthetic instance whose own rules
 *  attached further content) resolves iteratively — bounded, because each
 *  round only materializes overlay entries that already exist. */
export function mergeEffective(state: ConfigurationState, overlay: Overlay): ConfigurationState {
  const byId = new Map<string, ConfigInstance>();
  for (const inst of state.instances) {
    byId.set(inst.instanceId, applyInstanceOverlay(inst, overlay.instances[inst.instanceId]));
  }
  // Materialize synthetic instances for auto-attach entries whose host exists.
  let added = true;
  while (added) {
    added = false;
    for (const [hostId, io] of Object.entries(overlay.instances)) {
      if (!byId.has(hostId)) continue; // orphaned overlay bucket — host gone
      for (const entry of Object.values(io.forcedSlotContents)) {
        if (byId.has(entry.attachKey)) continue;
        const synthetic: ConfigInstance = {
          instanceId: entry.attachKey,
          productId: entry.productId,
          materialPicks: {},
          slotContents: {},
        };
        byId.set(
          entry.attachKey,
          applyInstanceOverlay(synthetic, overlay.instances[entry.attachKey]),
        );
        added = true;
      }
    }
  }
  return {
    instances: [...byId.values()],
    context: state.context,
    formValues: state.formValues,
    collectionPicks: state.collectionPicks,
  };
}
